import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useLayout } from '../../../contexts/LayoutContext';
import { getUserCredits, UserCredit } from '../../../services/credits';
import { signOut, isAdmin } from '../../../services/auth';

export default function Navbar() {
  const { user } = useAuth();
  const { setSidebarOpen } = useLayout();
  const [credits, setCredits] = useState<UserCredit | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  // 控制「关于」弹窗显隐
  const [showAboutDialog, setShowAboutDialog] = useState(false);

  useEffect(() => {
    if (user) {
      loadCredits();
    }
  }, [user]);

  useEffect(() => {
    const onCreditsUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{ userId?: string }>;
      const targetUserId = customEvent?.detail?.userId;
      if (!user) return;
      if (!targetUserId || targetUserId === user.id) {
        loadCredits();
      }
    };
    window.addEventListener('credits-updated', onCreditsUpdated as EventListener);
    return () => {
      window.removeEventListener('credits-updated', onCreditsUpdated as EventListener);
    };
  }, [user]);

  const loadCredits = async () => {
    if (!user) return;
    const userCreditsResult = await getUserCredits(user.id);
    setCredits(userCreditsResult.credits);
  };

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/login';
  };

  return (
    <nav className="bg-[#0a0a0a] border-b border-gray-800 px-4 sm:px-6 py-3 sm:py-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-800 cursor-pointer shrink-0 touch-manipulation"
            aria-label="打开菜单"
          >
            <i className="ri-menu-line text-xl"></i>
          </button>
          <Link
            to="/"
            className="text-base sm:text-xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent truncate"
          >
            AI创作平台
          </Link>
        </div>

        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          {user && credits != null && (
            <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-amber-900/30 to-orange-900/30 rounded-lg border border-amber-700/50">
              <i className="ri-coin-line text-amber-500 text-base sm:text-lg"></i>
              <span className="text-xs sm:text-sm font-semibold text-amber-400">{credits.credits} 积分</span>
            </div>
          )}

          {/* 关于按钮：位于登录按钮左侧 */}
          <button
            type="button"
            onClick={() => setShowAboutDialog(true)}
            className="px-3 sm:px-5 py-2 border border-gray-700 text-gray-300 rounded-lg font-medium hover:border-teal-500/60 hover:text-teal-400 hover:bg-teal-500/5 transition-all whitespace-nowrap text-sm sm:text-base touch-manipulation min-h-[44px] flex items-center justify-center gap-1.5 cursor-pointer"
          >
            <i className="ri-information-line text-base"></i>
            关于
          </button>

          {user ? (
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer touch-manipulation min-h-[44px]"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 flex items-center justify-center text-white text-sm font-medium shrink-0">
                  {user.name?.[0]?.toUpperCase() || 'U'}
                </div>
                <span className="hidden sm:inline text-sm font-medium text-gray-200 truncate max-w-[100px]">
                  {user.name || user.email}
                </span>
                <i className="ri-arrow-down-s-line text-gray-400 shrink-0"></i>
              </button>

              {showUserMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowUserMenu(false)}
                    aria-hidden
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-[#1a1a1a] rounded-lg shadow-lg border border-gray-700 py-2 z-50">
                    <button
                      onClick={handleSignOut}
                      className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-red-900/20 transition-colors cursor-pointer touch-manipulation flex items-center gap-2"
                    >
                      <i className="ri-logout-box-line"></i>
                      退出登录
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="px-4 sm:px-6 py-2 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg font-medium hover:from-teal-600 hover:to-emerald-600 transition-all whitespace-nowrap text-sm sm:text-base touch-manipulation min-h-[44px] flex items-center justify-center"
            >
              登录
            </Link>
          )}
        </div>
      </div>

      {/* 关于 DEERCALL 弹窗 */}
      {showAboutDialog && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4"
          onClick={() => setShowAboutDialog(false)}
        >
          <div
            className="bg-[#1a1a1a] rounded-2xl max-w-lg w-full border border-gray-800 relative shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="about-dialog-title"
          >
            {/* 顶部渐变装饰条 */}

            <div className="p-6 sm:p-8">
              {/* 关闭按钮 */}
              <button
                type="button"
                onClick={() => setShowAboutDialog(false)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-300 hover:bg-gray-800 transition-colors cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl"></i>
              </button>

              {/* 标题区 */}
              <div className="text-center mb-6 pr-6">
                <h2
                  id="about-dialog-title"
                  className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent"
                >
                  关于 DEERCALL
                </h2>
              </div>

              {/* 产品信息表 */}
              <div className="bg-[#0a0a0a] rounded-xl border border-gray-800 p-4 sm:p-5 mb-5 space-y-3">
                {[
                  { label: '软件名称', value: '人工智能视觉开发平台' },
                  { label: '产品标识', value: 'DEERCALL' },
                  { label: '版本号', value: 'AIVIDEO 2.0' },
                  { label: '开发者', value: '嘉兴金喆计算机有限公司' },
                  { label: '应用单位', value: '嘉兴职业技术学院' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-start gap-3 text-sm"
                  >
                    <span className="text-gray-500 shrink-0 w-[4.5rem] sm:w-20 pt-0.5">
                      {item.label}
                    </span>
                    <span className="text-gray-200 font-medium leading-relaxed">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* 介绍正文 */}
              <div className="space-y-3 text-sm text-gray-400 leading-relaxed text-justify">
                <p>
                  人工智能视觉开发平台是一款面向教育教学及数字内容创作场景开发的智能化平台。平台运用人工智能技术，为用户提供视觉内容策划、素材处理、视频生成及成果输出等功能，有效提升教学视频、宣传视频和数字化视觉内容的制作效率。
                </p>
                <p>
                  本平台由嘉兴金喆计算机有限公司为嘉兴职业技术学院开发并提供技术支持。
                </p>
              </div>

              {/* 版权页脚 */}
              <div className="mt-6 pt-4 border-t border-gray-800 text-center">
                <p className="text-xs text-gray-600 tracking-wide">
                  © 2026 嘉兴金喆计算机有限公司
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
